import React, { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../theme/useChartTokens';
import type { CampaignResultsPage } from '../outreachTypes';
import { streamsSourceName } from './resultsCopy';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import card from '../live/Campaign.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

type StreamsDay = { day: string; streams: number };
type Pin = CampaignResultsPage['pins'][number];

/** The axis days: the first, the release day, two in between, the last. */
function axisDays(days: StreamsDay[], releaseDay: string | null): string[] {
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) {
    return [];
  }
  const picks = new Set<string>([first.day, last.day]);
  if (releaseDay !== null && days.some((item) => item.day === releaseDay)) {
    picks.add(releaseDay);
  }
  [1, 2].forEach((part) => {
    const item = days[Math.floor((days.length * part) / 3)];
    if (item !== undefined) {
      picks.add(item.day);
    }
  });
  return days.map((item) => item.day).filter((day) => picks.has(day));
}

function dayText(intl: IntlShape, day: string): string {
  return intl.formatDate(new Date(`${day}T12:00:00Z`), { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

interface StreamsChartProps {
  songTitle: string;
  streams: StreamsDay[] | null;
  source: string | null;
  pins: Pin[];
  releaseDate: string | null;
}

/**
 * The song's streams every day (design outreach-v2 n1), with a pin on the release day ("OUT") and
 * on each day with adds ("+3"). The bars before the release are grey. With no streaming source:
 * "Connect Spotify for Artists". The chart uses recharts, as the other charts of the app do.
 */
const StreamsChartComponent: React.FC<StreamsChartProps> = ({ songTitle, streams, source, pins, releaseDate }) => {
  const intl = useIntl();
  const tokens = useChartTokens();
  const releaseDay = releaseDate?.slice(0, 10) ?? null;
  const days = useMemo(() => streams ?? [], [streams]);
  const pinMarks = useMemo(
    () =>
      pins
        .map((pin) => ({ pin, index: days.findIndex((item) => item.day === pin.day) }))
        .filter(({ index }) => index >= 0)
        .map(({ pin, index }) => (
          <span
            key={`${pin.day}-${pin.release ? 'out' : 'adds'}`}
            className={clsx(styles.pin, pin.release && styles.pinRelease)}
            style={{ left: `${((index + 0.5) / days.length) * 100}%` }}
            data-testid="streams-pin"
          >
            {pin.release
              ? intl.formatMessage(messages.pinOut)
              : intl.formatMessage(messages.pinAdds, { count: pin.adds })}
          </span>
        )),
    [days, intl, pins]
  );
  const axis = useMemo(
    () => axisDays(days, releaseDay).map((day) => <span key={day}>{dayText(intl, day)}</span>),
    [days, intl, releaseDay]
  );
  const cells = useMemo(
    () =>
      days.map((item) => (
        <Cell key={item.day} fill={releaseDay !== null && item.day < releaseDay ? tokens.flat : tokens.brandLift} />
      )),
    [days, releaseDay, tokens.brandLift, tokens.flat]
  );
  const summary = useMemo(() => {
    const last = days[days.length - 1]?.streams ?? 0;
    const total = days
      .filter((item) => releaseDay === null || item.day >= releaseDay)
      .reduce((sum, item) => sum + item.streams, 0);
    return { last, total, addDays: pins.filter((pin) => !pin.release).length };
  }, [days, pins, releaseDay]);

  const head = (
    <div className={card.cardHead}>
      <div>
        <h3 className={card.cardTitle}>{intl.formatMessage(messages.streamsTitle, { song: songTitle })}</h3>
        {streams !== null && (
          <p className={card.cardIntro}>
            {intl.formatMessage(messages.streamsIntro, { source: streamsSourceName(intl, source) })}
          </p>
        )}
      </div>
    </div>
  );

  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  if (streams === null || firstDay === undefined || lastDay === undefined) {
    return (
      <section className={card.card} data-testid="streams-chart">
        {head}
        <p className={`${shared.small} m-0`} data-testid="streams-none">
          {intl.formatMessage(messages.streamsNone)}
        </p>
      </section>
    );
  }

  return (
    <section className={card.card} data-testid="streams-chart">
      {head}
      <div
        className={styles.chart}
        role="img"
        aria-label={intl.formatMessage(messages.streamsChart, {
          from: dayText(intl, firstDay.day),
          to: dayText(intl, lastDay.day),
          adds: summary.addDays,
        })}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={days} margin={{ top: 18, right: 0, bottom: 0, left: 0 }} barCategoryGap={1}>
            <XAxis dataKey="day" hide />
            <YAxis hide />
            <Tooltip
              cursor={{ fill: tokens.cursor }}
              contentStyle={{ background: tokens.tooltipBg, border: 0, color: tokens.tooltipTx }}
              labelFormatter={(day: string) => dayText(intl, day)}
              formatter={(value: number) => intl.formatNumber(value)}
            />
            <Bar dataKey="streams" isAnimationActive={false} radius={[3, 3, 0, 0]}>
              {cells}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className={styles.pins} aria-hidden>
          {pinMarks}
        </div>
      </div>
      <div className={styles.axis} aria-hidden>
        {axis}
      </div>
      <p className={`${shared.small} m-0`}>
        {intl.formatMessage(messages.streamsNow, {
          perDay: intl.formatNumber(summary.last),
          total: intl.formatNumber(summary.total),
        })}
      </p>
    </section>
  );
};

export const StreamsChart = TypedMemo(StreamsChartComponent);
